import { rgba } from 'polished';
import { createGlobalStyle } from 'styled-components';

import { type Theme } from '@/utils/theme/themes';

import { colorThemeVars } from './color-theme';
import { cssReset } from './cssReset';

export const GlobalStyle = createGlobalStyle<{
  theme?: Theme;
}>`
  @layer reset, base, application, color-theme, external, utility;

  ${colorThemeVars}

  @layer reset {
    ${cssReset}
  }

  @layer base {
    body {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    * {
      box-sizing: border-box;
    }
  }

  @layer application {
    :root {
      --app-focus-outline: 2px solid ${({ theme }) => theme.paletteCssVars.fgA900};
      --app-focus-outline-offset: 2px;
    }

    html {
      scroll-behavior: smooth;
    }

    html, body {
      background-color: ${({ theme }) => theme.paletteCssVars.fg100};
    }
    
    body {
      -webkit-text-size-adjust: none;
      text-size-adjust: none;
      color: ${({ theme }) => theme.paletteCssVars.fg1200};
      font-family: ${({ theme }) => theme.fontFamily.SANS}, sans-serif;
    }

    * {
      caret-color: ${({ theme }) => theme.paletteCssVars.fg1200};
    }

    *::selection {
      background: ${({ theme }) => theme.paletteCssVars.fgA700};
    }

    *:focus-visible {
      outline: var(--app-focus-outline);
      outline-offset: var(--app-focus-outline-offset);
    }
    
    p {
      margin-bottom: 1em;
      margin-bottom: 1lh;
    }

    p:last-child {
      margin-bottom: 0;
    }

    h1, h2, h3, h4 {
      color: ${({ theme }) => theme.fontColor.LOUD};
    }
    
    h5, h6 {
      color: ${({ theme }) => theme.paletteCssVars.fg1100};
    }

    a {
      color: ${({ theme }) => theme.fontColor.DEFAULT};
      text-decoration: none;
      border-bottom: 1px solid ${({ theme }) => theme.paletteCssVars.fg1200};
      background-color: transparent;
      transition: background-color linear 0.1s;

      &:hover {
        text-decoration: none;
        background-color: ${({ theme }) => theme.paletteCssVars.fgA500};
      }

      &:active {
        background-color: ${({ theme }) => theme.paletteCssVars.fgA700};
      }
    }

    mark {
      background-color: ${({ theme }) => rgba(theme.palette.citrine, 0.32)};
      color: ${({ theme }) => theme.paletteCssVars.fgLoud};
      font-weight: 700;
    }

    hr {
      height: 1px;
      margin: 25px 0;
      border: none;
      background-color: ${({ theme }) => rgba(theme.colors.BLACK, 0.1)};
    }
    
    .smoothScroll {
      scroll-behavior: smooth;
    }

    .hexLogoLink {
      text-decoration: none;
      border-bottom: none;
    }

    .divider {
      height: 1px;
      margin: 25px 0;
      background-color: ${({ theme }) => rgba(theme.colors.BLACK, 0.1)};
    }

    .emoji {
      margin: 0 7px 0 3px;
    }

    .sr-only {
      position: absolute;
      left: -10000px;
      top: auto;
      width: 1px;
      height: 1px;
      overflow: hidden;
    }
  }

  @layer external {
    .toolTip {
      pointer-events: all !important;
      background: ${({ theme }) => theme.backgroundColor} !important;
      padding: 8px 10px !important;
      box-shadow: none !important;
      border-radius: 3px !important;
      z-index: 99999999999 !important;
      max-width: 215px !important;
      color: ${({ theme }) => theme.colors.WHITE} !important;
      font-size: ${({ theme }) => theme.fontSize.DEFAULT} !important;
      line-height: 20px !important;

      border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT} !important;
      box-shadow: 0 4px 4px ${({ theme }) =>
        rgba(theme.colors.BLACK, 0.25)}, 0 0 54px ${({ theme }) =>
        theme.colors.BLACK},
        0 0 50px ${({ theme }) =>
          rgba(theme.borderColor.DEFAULT, 0.25)} !important;

      .react-tooltip-arrow {
        display: none !important;
      }
    }
  }
`;
